import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import React, { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from "react";
import Address from "./Address";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { cn } from "@/lib/utils";
import axiosInstance from "@/utils/axiosInstance";

interface Props {
  vehicleGpsId: string | number;
  vehicleNickname: string;
  onRouteChanged: (trip: any) => void;
  closeListRoutes: any;
  isRouteOpen: boolean;
  setRouteOpen: Dispatch<SetStateAction<boolean>>
}

const VehicleRoutes: FC<Props> = ({
  vehicleGpsId,
  vehicleNickname,
  onRouteChanged: setSelectedRoute,
  closeListRoutes,
  isRouteOpen,
  setRouteOpen
}) => {
  const [routesData, setRoutesData] = useState<{
    endTrackPoint: {
      utc: string;
      position: { latitude: number, longitude: number }
    },
    startTrackPoint: {
      utc: string;
      position: { latitude: number, longitude: number }
    }
    totalDistance: number;
  }[]>([]);
  const [date, setDate] = useState<Date>(new Date());

  const lastFetchRef = useRef<{ userid: string | number; date: string } | null>(null);

  useEffect(() => {
    if (!date || !vehicleGpsId) return;

    const formattedDate = format(date, "yyyy-MM-dd");

    // Check if we've already fetched this exact combination
    if (
      lastFetchRef.current &&
      lastFetchRef.current.userid === vehicleGpsId &&
      lastFetchRef.current.date === formattedDate
    ) {
      return; // Skip fetch if same userid and date
    }

    const getVehicleRoutes = async () => {
      try {
        const url = `/gps-track/users/${vehicleGpsId}/tripinfos/${formattedDate}`;

        const response = await axiosInstance.get(url);

        if (!response.data) {
          throw new Error(`Error: ${response.status}`);
        }

        setRoutesData(response.data);
        // Store the fetched combination
        lastFetchRef.current = { userid: vehicleGpsId, date: formattedDate };
      } catch (error) {
        console.error("Fetch error:", error);
      }
    };

    getVehicleRoutes();
  }, [date, vehicleGpsId]);

  return isRouteOpen && (
    <div className="p-4 border rounded-xl shadow-lg bg-background relative w-[480px] sm:max-w-sm">
      <button
        onClick={() => {
          closeListRoutes(true);
          setRouteOpen(false)
        }}
        className="absolute top-3 right-3 text-gray-500 hover:text-red-500 cursor-pointer"
      >
        <X className="w-5 h-5" />
      </button>

      <h3 className="text-lg font-semibold mb-2">Vehicles {vehicleNickname} Routes</h3>

      <div className="mb-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !date && "text-muted-foreground",
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date ? format(date, "PPP") : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="flex w-auto flex-col space-y-2 p-2"
          >
            <div className="rounded-md border">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(newDate) => {
                  if (newDate) setDate(newDate);
                }}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <ul className="divide-y-2 divide-accent max-h-[60vh] overflow-y-auto pr-1">
        {routesData.length === 0 ? (
          <p className="text-sm text-gray-500 text-center mt-4">
            No routes for selected date.
          </p>
        ) : (
          routesData.reverse().map((trip, index: number) => (
            <li
              key={index}
              onClick={() => {
                setSelectedRoute(trip);
              }}
              className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition hover:bg-gray-100`}
            >
              <div className="flex items-center space-x-2">
                <Address
                  pickup_latitude={trip.startTrackPoint.position.latitude}
                  pickup_longitude={trip.startTrackPoint.position.longitude}
                  dropoff_latitude={trip.endTrackPoint.position.latitude}
                  dropoff_longitude={trip.endTrackPoint.position.longitude}
                  startTime={trip.startTrackPoint.utc}
                  endTime={trip.endTrackPoint.utc}
                />
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>{Math.round(trip.totalDistance / 1000)} km</span>
              </div>
            </li>
          ))
        )}
      </ul>
    </div>
  )
};

// Memoize component - primitive props won't cause unnecessary re-renders
export default React.memo(VehicleRoutes);
